import { useState } from 'react';
import { toast } from 'sonner';
import { X, Check, Lock, NotebookPen } from 'lucide-react';
import { EtatJeu, GameData, Invite, Maj } from '../game/types';
import { assetUrl } from '../game/logic';

interface Props {
  data: GameData;
  etat: EtatJeu;
  maj: Maj;
}

const SilhouetteVoilee = () => (
  <svg viewBox="0 0 90 120" className="w-full h-full" aria-hidden="true">
    <rect width="90" height="120" fill="#DCE5EF" />
    <ellipse cx="45" cy="42" rx="16" ry="19" fill="#A8BBD4" opacity="0.8" />
    <path d="M45 16 C 26 30 24 54 26 78 L 64 78 C 66 54 64 30 45 16 Z" fill="#A8BBD4" opacity="0.45" />
    <path d="M12 120 C 15 90 28 72 45 72 C 62 72 75 90 78 120 Z" fill="#A8BBD4" opacity="0.85" />
  </svg>
);

const PortraitInvite = ({ invite }: { invite: Invite }) => {
  if (invite.selfie) {
    return <img src={assetUrl(invite.selfie)} alt={invite.prenom} className="w-full h-full object-cover" />;
  }
  return (
    <div className="w-full h-full bg-casse flex flex-col items-center justify-center gap-2 p-3">
      <SilhouetteVoilee />
    </div>
  );
};

export default function Tatoues({ data, etat, maj }: Props) {
  const [ficheId, setFicheId] = useState<string | null>(null);

  const debloques = etat.permutation.slice(0, etat.questionsOk.length * 2);
  const ordonnes = etat.permutation
    .map((id) => data.invites.find((i) => i.id === id))
    .filter((i): i is Invite => !!i);
  const fiche = ficheId ? data.invites.find((i) => i.id === ficheId) : null;
  const collectes = etat.indicesCollectes;

  const marquerCollecte = (invite: Invite) => {
    maj((e) => ({ ...e, indicesCollectes: [...e.indicesCollectes, invite.id] }));
    toast.success(`L'indice de ${invite.prenom} est consigné dans votre carnet.`);
  };

  return (
    <div className="space-y-8">
      <section className="anim-apparition">
        <p className="petit-cap">Acte II — Les tatoués</p>
        <p className="text-sm text-encre-clair mt-2 leading-relaxed">
          Retrouvez ces invités en chair et en os : chacun vous confiera son indice à l'oral.
          Chaque bonne réponse dans la galerie en dévoile deux.
        </p>
        <div className="grid grid-cols-2 gap-4 mt-4">
          {ordonnes.map((invite) => {
            const ouvert = debloques.includes(invite.id);
            const collecte = collectes.includes(invite.id);
            return (
              <button
                key={invite.id}
                data-testid={`tatoue-card-${invite.id}`}
                disabled={!ouvert}
                onClick={() => setFicheId(invite.id)}
                className="text-left border border-encre bg-white overflow-hidden transition-transform duration-200 active:scale-[0.97] disabled:active:scale-100"
              >
                <div className="relative aspect-[3/4]">
                  {ouvert ? <PortraitInvite invite={invite} /> : <SilhouetteVoilee />}
                  {ouvert && (
                    <span className="absolute top-2 left-2 w-7 h-7 rounded-full bg-white border border-encre text-encre text-xs font-semibold flex items-center justify-center">
                      {invite.groupe}
                    </span>
                  )}
                  {collecte && (
                    <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-succes text-white flex items-center justify-center">
                      <Check size={16} />
                    </span>
                  )}
                  {!ouvert && (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <Lock size={22} className="text-encre-clair" />
                    </span>
                  )}
                </div>
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-encre truncate">
                    {ouvert ? invite.prenom : 'Voilé'}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <h3 className="petit-cap flex items-center gap-2">
          <NotebookPen size={14} /> Votre carnet
        </h3>
        {collectes.length === 0 ? (
          <p className="text-sm text-encre-clair mt-2">Aucun indice recueilli pour l'heure.</p>
        ) : (
          <ul className="mt-3 space-y-2" data-testid="carnet-liste">
            {collectes.map((id) => {
              const inv = data.invites.find((i) => i.id === id);
              if (!inv) return null;
              return (
                <li key={id} className="carte px-4 py-3">
                  <p className="text-sm font-medium text-encre">
                    {inv.prenom} <span className="text-encre-clair font-normal">· groupe {inv.groupe}</span>
                  </p>
                  <p className="text-sm text-texte mt-0.5 italic">{etat.carnet[id]?.trim() || 'Indice reçu, sans note.'}</p>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {fiche && (
        <div className="fixed inset-0 z-40 bg-white overflow-y-auto anim-apparition" data-testid="fiche-tatoue-overlay">
          <div className="max-w-md mx-auto px-6 py-6 pb-12">
            <button
              data-testid="fermer-tatoue-btn"
              onClick={() => setFicheId(null)}
              className="min-h-[44px] min-w-[44px] flex items-center gap-2 text-encre-clair font-medium"
            >
              <X size={20} /> Fermer
            </button>
            <div className="mt-4 border border-encre overflow-hidden">
              <div className="aspect-[3/4]">
                <PortraitInvite invite={fiche} />
              </div>
            </div>
            <h2 className="font-script text-5xl text-encre text-center mt-5">{fiche.prenom}</h2>
            <p className="petit-cap text-center mt-1">{fiche.role}</p>
            <p className="italic text-texte text-center mt-2">« {fiche.devise} »</p>
            <p className="text-center mt-2">
              <span className="inline-block px-3 py-1 rounded-full bg-brume text-encre text-xs font-semibold">
                Groupe {fiche.groupe}
              </span>
            </p>

            <label htmlFor="note" className="petit-cap block mt-8 mb-2">
              Votre note (facultative)
            </label>
            <textarea
              id="note"
              data-testid="carnet-note-input"
              rows={2}
              value={etat.carnet[fiche.id] || ''}
              onChange={(e) => {
                const v = e.target.value;
                maj((et) => ({ ...et, carnet: { ...et.carnet, [fiche.id]: v } }));
              }}
              placeholder="Notez ici l'indice reçu…"
              className="w-full px-4 py-3 rounded-lg border border-encre bg-white text-texte placeholder:text-encre-clair/60"
            />
            <p className="text-xs text-encre-clair mt-2">
              L'application n'affiche jamais l'indice : elle retient seulement que vous l'avez reçu.
            </p>

            {collectes.includes(fiche.id) ? (
              <div className="mt-4 min-h-[48px] rounded-full border border-succes text-succes font-medium flex items-center justify-center gap-2">
                <Check size={18} /> Indice recueilli
              </div>
            ) : (
              <button data-testid="indice-recu-btn" className="btn-principal mt-4" onClick={() => marquerCollecte(fiche)}>
                J'ai reçu son indice
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
